/**
 * SIDE PANEL: SCHOOL
 * Panel latéral pour consultation/édition d'une école
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Plus } from 'lucide-react';
import type { UpdateSchoolInput } from '../../schemas/school';
import { updateSchoolSchema } from '../../schemas/school';
import { COUNTRIES, POSITIONINGS, SCHOOL_TYPES, type School } from '../../types/school';
import type { Contact } from '../../types/contact';
import { Form, FormInput, FormSelect, FormTextarea, FormPhoneInput, toast } from '../ui';
import { SidePanel, SidePanelFooter } from '../ui/side-panel';
import { useSupabase } from '../../hooks/useSupabase';
import { usePermissions } from '../../hooks/usePermissions';
import { AssignContactModal } from '../modals';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../lib/routes/constants';

interface SchoolSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  school: School | null;
  onUpdate: (id: string, data: UpdateSchoolInput) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}

export function SchoolSidePanel({
  isOpen,
  onClose,
  school,
  onUpdate,
  onDelete,
}: SchoolSidePanelProps) {
  const supabase = useSupabase();
  const navigate = useNavigate();
  const { canDeleteEntity } = usePermissions();
  const [contacts, setContacts] = useState<
    Pick<Contact, 'id' | 'first_name' | 'last_name' | 'email' | 'phone' | 'status'>[]
  >([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [isAssignContactModalOpen, setIsAssignContactModalOpen] = useState(false);

  const form = useForm<UpdateSchoolInput>({
    resolver: zodResolver(updateSchoolSchema),
  });

  // Réinitialiser le formulaire quand l'école change
  useEffect(() => {
    if (school) {
      form.reset({
        name: school.name,
        postal_code: school.postal_code || '',
        city: school.city || '',
        country: school.country,
        type: school.type || '',
        positioning: school.positioning || '',
        address: school.address || '',
        notes: school.notes || '',
        contact_first_name: school.contact_first_name || '',
        contact_last_name: school.contact_last_name || '',
        contact_email: school.contact_email || '',
        contact_phone: school.contact_phone || '',
      });
    }
  }, [school, form]);

  // Charger les contacts associés à l'école
  const loadContacts = useCallback(async () => {
    if (!school) return;

    setContactsLoading(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone, status')
        .eq('school_id', school.id)
        .order('last_name', { ascending: true });

      if (error) throw error;
      setContacts(data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [school, supabase]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  if (!school) return null;

  const handleFormSubmit = async (data: UpdateSchoolInput) => {
    try {
      await onUpdate(school.id, data);
      // Ne pas fermer le panel pour permettre à l'utilisateur de continuer à modifier
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'école :", error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(school.id);
    }
  };

  // Assigner un contact à cette école
  async function handleAssignContact(contactId: string) {
    if (!school) return;

    try {
      const { error } = await supabase
        .from('contacts')
        .update({ school_id: school.id })
        .eq('id', contactId);

      if (error) throw error;

      toast.success("Contact associé à l'école");
      await loadContacts();
    } catch (error) {
      console.error('Error assigning contact:', error);
      toast.error("Erreur lors de l'association");
    }
  }

  // Retirer un contact de cette école
  async function handleRemoveContact(contactId: string) {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ school_id: null })
        .eq('id', contactId);

      if (error) throw error;

      toast.success("Contact retiré de l'école");
      await loadContacts();
    } catch (error) {
      console.error('Error removing contact:', error);
      toast.error('Erreur lors du retrait');
    }
  }

  // Naviguer vers la page de détail du contact
  function handleContactClick(contactId: string) {
    navigate(ROUTES.DATA.CONTACTS.DETAIL(contactId));
    onClose();
  }

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={school.name}
      onDelete={canDeleteEntity('school') && onDelete ? handleDelete : undefined}
      footer={
        <SidePanelFooter
          onCancel={handleClose}
          submitLabel="Enregistrer"
          isSubmitting={form.formState.isSubmitting}
          submitDisabled={form.formState.isDirty ? false : true}
          formId="edit-school-form"
        />
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} id="edit-school-form">
          <div className="space-y-6">
            {/* Section Informations générales */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Informations générales</h3>

              <div className="space-y-4">
                {/* Nom & Type en 2 colonnes */}
                <div className="grid grid-cols-2 gap-4">
                  <FormInput control={form.control} name="name" label="Nom" required />

                  <FormSelect
                    control={form.control}
                    name="type"
                    label="Type"
                    placeholder="Sélectionner un type"
                    options={SCHOOL_TYPES.map((type) => ({ value: type, label: type }))}
                  />
                </div>

                {/* Positionnement */}
                <FormSelect
                  control={form.control}
                  name="positioning"
                  label="Positionnement"
                  placeholder="Sélectionner un positionnement"
                  options={POSITIONINGS.map((p) => ({ value: p, label: p }))}
                />
              </div>
            </div>

            {/* Section Personne à contacter */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Personne à contacter</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    control={form.control}
                    name="contact_first_name"
                    label="Prénom"
                    placeholder="Prénom"
                  />
                  <FormInput
                    control={form.control}
                    name="contact_last_name"
                    label="Nom"
                    placeholder="Nom"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    control={form.control}
                    name="contact_email"
                    label="Email"
                    type="email"
                    placeholder="email@exemple.com"
                  />
                  <FormPhoneInput control={form.control} name="contact_phone" label="Téléphone" />
                </div>
              </div>
            </div>

            {/* Section Adresse */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-4">Adresse</h3>
              <div className="space-y-4">
                {/* Adresse complète */}
                <FormInput
                  control={form.control}
                  name="address"
                  label="Adresse"
                  placeholder="12 Av Trudaine"
                />

                {/* Ville, Code Postal, Pays en 3 colonnes */}
                <div className="grid grid-cols-3 gap-4">
                  <FormInput control={form.control} name="city" label="Ville" placeholder="Paris" />

                  <FormInput
                    control={form.control}
                    name="postal_code"
                    label="Code Postal"
                    placeholder="75009"
                  />

                  <FormSelect
                    control={form.control}
                    name="country"
                    label="Pays"
                    required
                    options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
                  />
                </div>

                {/* Notes */}
                <FormTextarea
                  control={form.control}
                  name="notes"
                  label="Notes"
                  placeholder="Ajouter une note..."
                  rows={4}
                />
              </div>
            </div>

            {/* Section Contacts associés */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-zinc-900">Contacts associés</h3>
                <button
                  type="button"
                  onClick={() => setIsAssignContactModalOpen(true)}
                  className="flex items-center justify-center size-9 rounded-md bg-zinc-900 hover:bg-zinc-800 transition-colors"
                  aria-label="Associer un contact"
                  title="Associer un contact"
                >
                  <Plus className="size-4 text-white" />
                </button>
              </div>

              {/* Liste des contacts */}
              {contactsLoading ? (
                <div className="text-sm text-zinc-500 py-4">Chargement...</div>
              ) : contacts.length === 0 ? (
                <div className="text-sm text-zinc-500 py-4 italic">
                  Aucun contact associé à cette école
                </div>
              ) : (
                <div className="space-y-2">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50"
                    >
                      <button
                        type="button"
                        onClick={() => handleContactClick(contact.id)}
                        className="flex-1 text-left"
                      >
                        <div className="font-medium text-zinc-900">
                          {contact.first_name} {contact.last_name}
                        </div>
                        <div className="text-sm text-zinc-500">{contact.email}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveContact(contact.id)}
                        className="text-sm text-zinc-400 hover:text-red-500 px-2"
                        title="Retirer de l'école"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>

      {/* Modale Associer un contact */}
      <AssignContactModal
        isOpen={isAssignContactModalOpen}
        onClose={() => setIsAssignContactModalOpen(false)}
        onAssign={handleAssignContact}
        excludeContactIds={contacts.map((c) => c.id)}
      />
    </SidePanel>
  );
}
